import { CrudSaveComponent } from '../_directives';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  CRM,
  CRMCampaign,
  CRMTypeId,
  FunnelFulfillmentType,
  ImageSize,
  Pager,
  Region,
  Audience,
  AudienceBillingCycleOptions,
  AudienceCardTypeOptions,
  AudiencePaymentTypeOptions,
  AudienceTrafficSourceOptions,
  AudienceConditionType,
  AudienceBillingCycle,
  getCampaignProductImageUrl,
  BillingCycleNoMax,
  BillingCycleCustomMin,
  AudienceFilterDescriptions,
  ProductSortOption,
  ProductSortOptionLabels,
  CampaignProduct,
  AudienceDropdownSettings,
  AudienceProductBatchSize,
  getCRMCampaignLabel,
  splitAudienceIds,
} from '../_models';
import {
  AlertService,
  AudienceService,
  CampaignProductService,
  CRMCampaignService,
  CRMService,
  RegionService,
  StorageService,
} from '../_services';
import { FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { takeUntil } from 'rxjs/operators';
import { config } from '../../config/config';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './audience-edit.component.html',
  styleUrls: ['./audience-edit.component.scss'],
  selector: 'app-audience-new',
  encapsulation: ViewEncapsulation.None,
})
export class AudienceNewComponent extends CrudSaveComponent implements OnInit {
  private isPublishing: {} = {};
  pageTitle = 'Create Audience';
  description =
    'Chose and configure conditions to narrow your audience. You can add multiple conditions to build highly specific customer segments.';
  popupTitle: string = 'Add a condition';
  isNewComponent: boolean = true;
  FunnelFulfillmentType = FunnelFulfillmentType;
  audience: Audience;
  availableCountries: { id: number | string; text: string }[] = [];
  AudienceConditionType = AudienceConditionType;
  AudienceBillingCycle = AudienceBillingCycle;
  AudienceFilterDescriptions = AudienceFilterDescriptions;
  billingOptions: number[] = AudienceBillingCycleOptions;
  filteredMaxBillingOptions: number[] = this.billingOptions;
  crms: CRM[];
  allCRMCampaigns: CRMCampaign[];
  selectedCRMCampaigns: {} = {};
  CRMCampaigns: {} = {};
  isProductFiltersSelected: boolean = false;
  selectedCRMId = null;
  products: any[] = null;
  fulfillmentTypeSelected: FunnelFulfillmentType = null;
  selectedStates = [];
  selectedCountries = [];
  selectedCampaigns = [];
  showSortMenu = false;
  selectedFilter: ProductSortOption;
  ProductSortOptionsList: any[] = [];
  selectedProducts: any[] = [];
  availableStates: { id: number; text: string }[] = [];
  availableCampaigns: { id: number; text: string }[] = [];
  dropdownSettings = AudienceDropdownSettings;
  billingCycleObject = {
    minimum: 1,
    maximum: null,
    isCustom: false,
  };
  transactionFiltersObject = {
    is3dsVerified: false,
    merchantId: null,
  };
  creditCardTypes = AudienceCardTypeOptions.map((option) => ({ ...option, value: false }));
  paymentTypes = AudiencePaymentTypeOptions.map((option) => ({ ...option, value: false }));
  trafficSources = AudienceTrafficSourceOptions.map((option) => ({ ...option, value: false }));
  conditions = {
    fulfillmentType: '',
    billingCycle: '',
    transactionFilters: '',
    location: '',
    campaignFilters: '',
    productFilters: '',
    trafficSource: '',
  };
  crmCampaignsMap: { [key: number]: any } = {};
  productsModalInput: any[] = null;
  page: number = 1;
  pageSize: number = config.defaultPageSize;
  pageCount: number = 1;
  totalResultCount: number = 0;
  Math = Math;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected audienceService: AudienceService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected regionService: RegionService,
    protected crmService: CRMService,
    protected crmCampaignService: CRMCampaignService,
    protected campaignProductService: CampaignProductService,
    protected modalService: NgxSmartModalService,
    protected storageService: StorageService
  ) {
    super(router, location, route, audienceService, alertService);
    this.isNew = true;
    this.objectName = 'audience';
  }

  ngOnInit() {
    this.form = this.formBuilder.group(
      {
        name: [null, [Validators.required]],
        use_product_filter: [false],
        is_3ds: [false],
        min_billing_cycle: [1, [Validators.required, Validators.min(1)]],
        max_billing_cycle: [null],
        merchant_ids: [null],
        crm_campaigns: [null],
        fulfillment_type: [null],
        states: [null],
        countries: [null],
        cc_types: [null],
        use_crm_campaign_filter: [false],
        traffic_sources: [null],
        aff_ids: [null],
        payment_types: [null],
      },
      {
        validator: this.minLessThanMaxValidator('min_billing_cycle', 'max_billing_cycle'),
      }
    );
    super.ngOnInit();

    Object.keys(ProductSortOptionLabels).forEach((key) => {
      this.ProductSortOptionsList.push({
        value: parseInt(key, 10),
        label: ProductSortOptionLabels[key],
      });
    });

    this.getCountries();
    this.updateStates();

    forkJoin([
      this.crmService.list({
        page: 1,
        page_size: config.maxPageSize,
        'type!': CRMTypeId.Test,
      }),
      this.crmCampaignService.list({
        page: 1,
        page_size: config.maxPageSize,
        'crm__type!': CRMTypeId.Test,
      }),
    ]).subscribe(
      (data: [Pager, Pager]) => {
        this.crms = data[0].results;

        this.allCRMCampaigns = data[1].results;
        this.buildCRMCampaignLists();
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  protected getFormData(): any {
    let merchantIds = null;
    let affIds = null;
    let states = null;
    let countries = null;
    let cardTypes = null;
    let paymentTypes = null;
    let trafficSources = [];
    let crmCampaigns = [];

    if (!this.fulfillmentTypeSelected) {
      this.form.value.fulfillment_type = null;
    }

    if (!this.billingCycleObject.maximum) {
      this.form.value.max_billing_cycle = null;
    }

    if (!this.transactionFiltersObject.is3dsVerified) {
      this.form.value.is_3ds = false;
    }

    if (!this.transactionFiltersObject.merchantId) {
      this.form.value.merchant_ids = null;
    }

    const selectedCreditCards = this.getSelectedTypes(this.creditCardTypes);
    const selectedPaymentTypes = this.getSelectedTypes(this.paymentTypes);
    const selectedTrafficSources = this.getSelectedTypes(this.trafficSources);

    if (selectedCreditCards.length) {
      cardTypes = selectedCreditCards;
    }

    if (selectedPaymentTypes.length) {
      paymentTypes = selectedPaymentTypes;
    }

    if (selectedTrafficSources.length) {
      trafficSources = selectedTrafficSources;
    }

    if (this.selectedStates.length) {
      states = [];
      this.form.value.states.forEach((state: { id: number; text: string }) => {
        states.push(state.id);
      });
    }

    if (this.selectedCountries && this.selectedCountries.length) {
      countries = [];
      this.form.value.countries.forEach((country: { id: number; text: string }) => {
        countries.push(country.id);
      });
    }

    if (this.selectedCampaigns.length) {
      this.selectedCampaigns.forEach((campaign: { id: number; text: string }) => {
        crmCampaigns.push(campaign.id);
      });
      this.form.value.use_crm_campaign_filter = true;
    } else {
      this.form.value.use_crm_campaign_filter = false;
      crmCampaigns = [];
    }

    if (this.products && this.products.length && this.isProductFiltersSelected) {
      this.form.value.use_product_filter = true;
    } else {
      this.form.value.use_product_filter = false;
    }

    if (this.form.value.merchant_ids && this.form.value.merchant_ids.length) {
      merchantIds = splitAudienceIds(this.form.value.merchant_ids);
    }

    if (this.form.value.aff_ids && this.form.value.aff_ids.length) {
      affIds = splitAudienceIds(this.form.value.aff_ids);
    }

    const filters = {
      merchant_ids: merchantIds,
      states: states,
      countries: countries,
      cc_types: cardTypes,
      traffic_sources: trafficSources,
      crm_campaigns: crmCampaigns,
      aff_ids: affIds,
      payment_types: paymentTypes,
    };

    return Object.assign({}, this.form.value, filters);
  }

  protected buildCRMCampaignLists() {
    if (this.crms && this.allCRMCampaigns) {
      const selectedCRMCampaigns = {};

      // initialize empty crm campaign arrays for each crm
      this.crms.forEach((crm: CRM) => {
        this.selectedCRMCampaigns[crm.id] = [];
        this.CRMCampaigns[crm.id] = [];
      });

      if (this.crms.length === 1) {
        this.selectedCRMId = this.crms[0].id;
      }

      // build a map of the selected crm campaign ids for faster lookup
      if (this.audience && this.audience.crm_campaigns) {
        for (const campaign_id of this.audience.crm_campaigns) {
          selectedCRMCampaigns[campaign_id] = true;
        }
      }

      // fill in the list of crm campaigns for each crm
      this.allCRMCampaigns.forEach((campaign: CRMCampaign) => {
        if (campaign.id in selectedCRMCampaigns) {
          this.selectedCRMCampaigns[campaign.crm].push(campaign);
        }

        this.CRMCampaigns[campaign.crm].push(campaign);
        this.crmCampaignsMap[campaign.id] = campaign;
      });

      const availableCampaigns = [];

      this.allCRMCampaigns.forEach((campaign: CRMCampaign) => {
        availableCampaigns.push({
          id: campaign.id,
          text: getCRMCampaignLabel(campaign),
        });
      });
      this.availableCampaigns = availableCampaigns;

      this.getProducts();
    }
  }

  navigateBackToAudiences() {
    this.router.navigate(['audiences']);
  }

  getCountries() {
    this.regionService.getCountries().subscribe((countries) => {
      this.availableCountries = countries
        .filter(({ id }) => id === 'us' || id === 'ca')
        .map(({ id, name }) => ({ id, text: name }));
    });
  }

  updateStates() {
    this.loading = true;
    this.availableStates = [];

    if (!this.selectedCountries.length && !(this.form.value.countries && this.form.value.countries.length)) {
      this.availableStates = [];
      this.selectedStates = [];
      this.loading = false;
    } else {
      const stateRequests = this.selectedCountries.map((country) => this.regionService.list(country.id));

      forkJoin(stateRequests).subscribe((responses: Region[][]) => {
        const availableStates = [];

        responses.forEach((states) => {
          states.forEach((state: Region) => {
            availableStates.push({ id: state.id, text: state.name });
          });
        });

        this.availableStates = availableStates;
        this.loading = false;
      });
    }
  }

  onConditionSelect(conditionType: string) {
    switch (conditionType) {
      case AudienceConditionType.FulfillmentType:
        this.conditions.fulfillmentType = AudienceConditionType.FulfillmentType;
        break;
      case AudienceConditionType.BillingCycle:
        this.conditions.billingCycle = AudienceConditionType.BillingCycle;
        break;
      case AudienceConditionType.TransactionFilters:
        this.conditions.transactionFilters = AudienceConditionType.TransactionFilters;
        break;
      case AudienceConditionType.Location:
        this.conditions.location = AudienceConditionType.Location;
        break;
      case AudienceConditionType.CampaignFilters:
        this.conditions.campaignFilters = AudienceConditionType.CampaignFilters;
        break;
      case AudienceConditionType.ProductFilters:
        this.conditions.productFilters = AudienceConditionType.ProductFilters;
        break;
      case AudienceConditionType.TrafficSource:
        this.conditions.trafficSource = AudienceConditionType.TrafficSource;
        break;
      default:
        break;
    }

    this.modalService.getModal('conditionsPopup').close();
  }

  onConditionRemove(conditionType: string) {
    switch (conditionType) {
      case AudienceConditionType.FulfillmentType:
        this.conditions.fulfillmentType = '';
        this.fulfillmentTypeSelected = null;
        this.form.value.fulfillment_type = null;
        break;
      case AudienceConditionType.BillingCycle:
        this.conditions.billingCycle = '';
        this.billingCycleObject.minimum = 1;
        this.form.get('min_billing_cycle').setValue(1);
        this.billingCycleObject.maximum = null;
        this.billingCycleObject.isCustom = false;
        break;
      case AudienceConditionType.TransactionFilters:
        this.conditions.transactionFilters = '';
        this.clearTransactionFilters();
        break;
      case AudienceConditionType.Location:
        this.conditions.location = '';
        this.selectedStates = [];
        this.selectedCountries = [];
        this.form.get('countries').setValue([]);
        this.form.get('states').setValue([]);
        break;
      case AudienceConditionType.CampaignFilters:
        this.conditions.campaignFilters = '';
        // if condition is closed we remove selected filters
        this.crms.forEach((crm: CRM) => {
          this.selectedCRMCampaigns[crm.id] = [];
        });
        this.selectedCampaigns = [];
        break;
      case AudienceConditionType.ProductFilters:
        this.conditions.productFilters = '';
        this.isProductFiltersSelected = false;
        if (this.products.length) {
          this.loading = true;
          // if product condition is closed we delete all the selected products
          let productIds = this.products.map((product) => product.id);
          this.removeProductsFromAudience(productIds);
          this.products = [];
        }
        break;
      case AudienceConditionType.TrafficSource:
        this.conditions.trafficSource = '';
        this.clearTrafficSourceFilters();
        break;
      default:
        break;
    }
  }

  onAllConditionsSelected(): boolean {
    return Object.entries(this.conditions).every(([key, value]) => {
      if (this.isNew && key === 'productFilters') {
        return true;
      }
      return value !== '';
    });
  }

  isConditionSelected(): boolean {
    const formData = this.getFormData();

    return Object.entries(formData).some(([key, value]) => {
      if (key === 'max_billing_cycle' || key === 'name') {
        return false;
      }

      // Check if the value is not null, not false, and not an empty array
      if (value !== null && value !== false && (!Array.isArray(value) || value.length > 0)) {
        return true;
      }

      return false;
    });
  }

  closeConditionsPopup() {
    this.modalService.getModal('conditionsPopup').close();
  }

  openConditionsPopup() {
    if (!this.audience) {
      this.createAudience();
    } else {
      this.modalService.getModal('conditionsPopup').open();
    }
  }

  selectCustom() {
    this.billingCycleObject.isCustom = true;
    this.billingCycleObject.maximum = BillingCycleCustomMin;
  }

  clearTransactionFilters() {
    this.transactionFiltersObject.is3dsVerified = false;
    this.transactionFiltersObject.merchantId = null;
    this.creditCardTypes.forEach((type) => {
      type.value = false;
    });
    this.paymentTypes.forEach((type) => {
      type.value = false;
    });
  }

  clearTrafficSourceFilters() {
    this.trafficSources.forEach((type) => {
      type.value = false;
    });
  }

  onbillingCycleObject(selected: AudienceBillingCycle) {
    this.billingCycleObject.minimum = 1;
    this.form.get('min_billing_cycle').setValue(1);
    switch (selected) {
      case AudienceBillingCycle.One:
        this.billingCycleObject.maximum = 1;
        this.billingCycleObject.isCustom = false;
        break;
      case AudienceBillingCycle.Two:
        this.billingCycleObject.maximum = 2;
        this.billingCycleObject.isCustom = false;
        break;
      case AudienceBillingCycle.Three:
        this.billingCycleObject.maximum = 3;
        this.billingCycleObject.isCustom = false;
        break;
      default:
        break;
    }
  }

  onMaxRangeChange(value: number) {
    this.billingCycleObject.isCustom = true;
    this.billingCycleObject.maximum = value;
  }

  onMinRangeChange() {
    let minCycle = Number(this.form.get('min_billing_cycle').value);
    let maxCycle = Number(this.form.get('max_billing_cycle').value);

    this.filteredMaxBillingOptions = this.billingOptions.filter((value) => value >= minCycle);

    if (minCycle === BillingCycleNoMax) {
      maxCycle = null;
    } else if (minCycle > maxCycle) {
      maxCycle = minCycle + 1;
    }

    this.form.patchValue({
      min_billing_cycle: minCycle,
      max_billing_cycle: maxCycle,
    });

    this.form.updateValueAndValidity();
  }

  private getSelectedTypes(types: any): number[] {
    const typesArray: number[] = [];

    types.forEach((type) => {
      if (type.value === true) {
        typesArray.push(type.id);
      }
    });

    return typesArray;
  }

  addProductFilters() {
    if (!this.audience) {
      this.createAudience();
    } else {
      this.isProductFiltersSelected = true;
      this.getProducts();
      this.modalService.getModal('productsDialog').open();
    }
  }

  protected getProducts() {
    if (this.audience) {
      this.loading = true;

      this.campaignProductService
        .list({
          audiences: this.audience.id,
          page: this.page,
          page_size: this.pageSize,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (page: Pager) => {
            const productResults = page.results.length ? page.results : [];
            this.products = productResults.map((product) => {
              const matched =
                product.default_crm_campaign && this.crmCampaignsMap[product.default_crm_campaign]
                  ? this.crmCampaignsMap[product.default_crm_campaign]
                  : {};

              return {
                ...product,
                crm_campaign_id: matched.crm_campaign_id || null,
                crm_campaign_name: matched.name || null,
              };
            });

            this.pageCount = page.num_pages;
            this.totalResultCount = page.count;
            this.loading = false;
          },
          (error) => {
            this.loading = false;
            this.handleError(error);
          }
        );
    }
  }

  setPage(page: number) {
    if (page >= 1 && page <= this.pageCount) {
      this.page = page;
      this.getProducts();
    }
  }

  getPages(): number[] {
    const pages: number[] = [];
    let startPage = Math.max(1, this.page - 2);
    let endPage = Math.min(this.pageCount, this.page + 2);

    if (this.page <= 3) {
      endPage = Math.min(5, this.pageCount);
    }

    if (this.page > this.pageCount - 3) {
      startPage = Math.max(1, this.pageCount - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  getProductImageUrl(product) {
    return getCampaignProductImageUrl(product, ImageSize.small);
  }

  removeProductsFromAudience(productIds: number[]) {
    this.loading = true;
    this.audienceService
      .remove_campaign_products(this.audience.id, {
        campaign_products: productIds,
      })
      .subscribe(
        (_) => {
          this.loading = false;
          this.getProducts();
        },
        (error) => {
          this.loading = false;
          this.handleError(error);
        }
      );
  }

  deleteSelectedProducts(): void {
    if (this.selectedProducts.length) {
      let productIds = this.selectedProducts.map((product) => product.id);
      this.removeProductsFromAudience(productIds);
      this.selectedProducts = [];
    }
  }

  onProductsSelected(productIds: string[] | number[]) {
    this.modalService.getModal('productsDialog').close();
    this.addProductsToFunnel(productIds);
  }

  private populateCampaignProductRequests(productIds: string[] | number[]) {
    const requests = [];
    while (productIds.length > 0) {
      requests.push(
        this.audienceService.add_campaign_products(this.audience.id, {
          campaign_products: productIds.splice(0, AudienceProductBatchSize),
        })
      );
    }

    return requests;
  }

  addProductsToFunnel(productIds: string[] | number[]) {
    forkJoin(this.populateCampaignProductRequests(productIds)).subscribe(
      (_) => {
        this.loading = false;
        this.getProducts();
      },
      (error) => {
        this.loading = false;
        this.handleError(error);
      }
    );
  }

  createAudience() {
    this.submitted = true;

    if (this.form.valid) {
      this.loading = true;
      this.crudService.create(this.getFormData()).subscribe(
        (data: Audience) => {
          if ('id' in data) {
            this.id = data['id'];
          }
          this.audience = data;

          this.loading = false;
          this.isNew = false;
          this.isNewComponent = false;
          this.modalService.getModal('conditionsPopup').open();
          this.canDeactivateService.setFormDirty(false);
        },
        (error) => {
          this.handleSubmitError(error);
          this.loading = false;
        }
      );
    }
  }

  publish() {
    const audienceId = this.id || this.audience.id;
    if (audienceId) {
      this.audienceService.publish(audienceId).subscribe(
        () => {
          this.loading = false;
          this.alertService.success('Audience Published Successfully.', true);
          this.navigate(['/audiences'], { replaceUrl: true });
        },
        (error) => {
          this.handleError(error);
          this.loading = false;
        }
      );
    }
  }

  protected onSaveComplete(data) {
    this.publish();

    // Save the audience id to local storage
    this.storageService.set('audienceId', data.id);
  }

  isAudiencePublishing(audience: Audience) {
    return audience.id in this.isPublishing && this.isPublishing[audience.id];
  }

  isAudiencePublished(audience: Audience): boolean {
    return audience ? audience.is_modified : false;
  }

  minLessThanMaxValidator(minKey: string, maxKey: string): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const minControl = formGroup.controls[minKey];
      const maxControl = formGroup.controls[maxKey];

      if (minControl.value != null && maxControl.value != null && +minControl.value > +maxControl.value) {
        return { minGreaterThanMax: true };
      }

      return null;
    };
  }

  onProductToggle(product: any, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectedProducts.push(product);
    } else {
      this.selectedProducts = this.selectedProducts.filter((p) => p.id !== product.id);
    }
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.selectedProducts = [...this.products];
    } else {
      this.selectedProducts = [];
    }
  }

  isProductSelected(productId: number): boolean {
    return this.selectedProducts.some((p) => p.id === productId);
  }

  applyFilter(value: ProductSortOption): void {
    let filteredData = [...this.products];
    this.selectedFilter = value;

    switch (this.selectedFilter) {
      case ProductSortOption.ProductName:
        filteredData.sort((a, b) => a.name.localeCompare(b.name));
        break;

      case ProductSortOption.ProductId:
        filteredData.sort((a, b) => a.campaign_product_id - b.campaign_product_id);
        break;

      case ProductSortOption.CampaignName:
        filteredData.sort((a, b) => (a.crm_campaign_name || '').localeCompare(b.crm_campaign_name || ''));
        break;

      case ProductSortOption.CampaignId:
        filteredData.sort((a, b) => a.crm_campaign_id - b.crm_campaign_id);
        break;

      case ProductSortOption.OfferId:
        filteredData.sort((a, b) => (a.offer_id || 0) - (b.offer_id || 0));
        break;
    }

    this.products = filteredData;
  }

  toggleSortMenu() {
    this.showSortMenu = !this.showSortMenu;
  }

  get selectedSortOption() {
    return ProductSortOptionLabels[this.selectedFilter];
  }

  closeSortMenu() {
    this.showSortMenu = false;
  }

  onImageError(event: Event, product: CampaignProduct) {
    const img = event.target as HTMLImageElement;

    if (!img.dataset.fallbackApplied) {
      img.dataset.fallbackApplied = 'true';
      img.src = getCampaignProductImageUrl(product, ImageSize.original);
    } else {
      img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    }
  }
}
